import { useRef, useState } from 'react';
import {
  Button,
  Caption,
  List,
  Placeholder,
  Section,
} from '@telegram-apps/telegram-ui';
import { PrepareShareResponse } from '@veylme/shared';

import { apiFetch, emitEvent } from '../api/client.js';
import {
  hapticNotification,
  isPremiumUser,
  shareMessageOrUnsupported,
  shareToStoryOrUnsupported,
} from '../lib/tg.js';
import { humanizeApiError } from '../lib/apiError.js';
import { useToast } from '../components/ToastProvider.js';
import { useT } from '../hooks/useT.js';
import { useBackButton } from '../lib/nativeUi.js';
import styles from './ShareScreen.module.css';

/**
 * ShareScreen — success-state после POST /answers с двумя путями шеринга
 * answer-карточки (Веха 6, замыкание петли §6.3):
 *  - «Поделиться в Story» → shareStory(cardImageUrl) — фолбэки F1 (бренд вшит)
 *    + F2 (text + widgetLink, кликабелен только у Premium).
 *  - «Поделиться в чат» → POST /share/prepare {answer} → shareMessage(id) —
 *    фолбэк F3, единственный кликабельный путь для не-Premium.
 *
 * CANON G2/I4: оба вызова SDK строго в onClick (никаких useEffect/таймеров).
 * CANON G3/Q3: на 'unsupported'/'failed' НЕ падаем — показываем UX-подсказку.
 *
 * `cardImageUrl === null` (render не успел / упал, CANON F4 fail-soft) — fail-state
 * с возвратом в инбокс (явный retry POST /answers статуса — Веха 10).
 *
 * Веха 13: нативный BackButton, kit Button (filled/bezeled — ЗАЯВЛЕННОЕ ИСКЛЮЧЕНИЕ:
 * MainButton НЕ применяем, т.к. два равноправных действия). Логика share-флоу и
 * haptics (success/error) не изменены.
 */
export interface ShareScreenProps {
  cardImageUrl: string | null;
  answerId: string;
  /** Story-tagged deep-link автора (`bs<code>`, work-035) для widgetLink shareStory. */
  storyShareUrl: string | null;
  onBackToInbox: () => void;
}

type ShareState =
  | { kind: 'idle' }
  | { kind: 'preparing' }
  | { kind: 'sharing-story' }
  | { kind: 'sharing-message' }
  | { kind: 'notice'; tone: 'info' | 'error'; text: string };

export function ShareScreen({
  cardImageUrl,
  answerId,
  storyShareUrl,
  onBackToInbox,
}: ShareScreenProps) {
  const t = useT();
  const { showError } = useToast();
  const [state, setState] = useState<ShareState>({ kind: 'idle' });
  // Веха 12 §16.3: синхронный in-flight гард. `disabled={busy}` снимается только
  // на следующий ре-рендер → два очень быстрых клика проскочат и дадут двойной
  // POST /share/prepare + двойной emitEvent (засор K-метрик). ref ставится/читается
  // синхронно в начале хендлера, до setState, — закрывает гонку наглухо.
  const inFlight = useRef(false);

  useBackButton(onBackToInbox);

  const busy =
    state.kind === 'preparing' ||
    state.kind === 'sharing-story' ||
    state.kind === 'sharing-message';

  function handleShareStory() {
    if (cardImageUrl === null || inFlight.current) return;
    inFlight.current = true;
    setState({ kind: 'sharing-story' });

    // W2: Premium-gate — передаём widgetLink только Premium-пользователям.
    // Для не-Premium: widgetLinkUrl = '' → обёртка опускает widgetLink, нативный
    // апселл Telegram не выскочит. Показываем мягкую подсказку (тост).
    const premium = isPremiumUser();
    const result = shareToStoryOrUnsupported(cardImageUrl, {
      text: t.share_answered_text,
      widgetLinkUrl: premium ? (storyShareUrl ?? '') : '',
      widgetLinkName: t.share_ask_action,
    });

    if (result === 'shared') {
      hapticNotification('success');
      // Для не-Premium: мягкая подсказка про отсутствие ссылки (ненавязчивая,
      // без tone:'error' — это ожидаемое поведение, не ошибка). Тост живёт в
      // ToastProvider над экранами — переживает возврат в инбокс ниже.
      if (!premium) {
        showError(t.share_story_link_premium_note);
      }
      // Веха 8: авторское share-событие (CANON G2 — в onClick после результата).
      // work-031 М1: answerId — для серверной корреляции Answer.sharedToStory.
      void emitEvent({ type: 'answer_shared_to_story', channel: 'story', answerId });
      // shareToStory лишь ОТКРЫВАЕТ нативный редактор Story поверх мини-аппа и не
      // сообщает, опубликовал ли автор историю (web_app_share_to_story без
      // callback — сверено с core.telegram.org/bots/webapps, 2026-06-03). Поэтому
      // не зависаем на success-экране со «статусом-полуфабрикатом» — сразу
      // возвращаемся в инбокс. Повторный шеринг (если автор передумал в редакторе)
      // доступен снова через «Поделиться» у ответа. Чат — отдельным заходом.
      inFlight.current = false;
      onBackToInbox();
      return;
    } else if (result === 'unsupported') {
      setState({ kind: 'notice', tone: 'info', text: t.share_story_unsupported });
    } else {
      hapticNotification('error');
      setState({ kind: 'notice', tone: 'error', text: t.share_story_error });
    }
    // Синхронный флоу завершён — снимаем гард (разрешаем legit повторный шеринг,
    // напр. сначала в Story, потом в чат).
    inFlight.current = false;
  }

  async function handleShareChat() {
    if (cardImageUrl === null || inFlight.current) return;
    inFlight.current = true;
    setState({ kind: 'preparing' });
    try {
      const prepared = await apiFetch('/share/prepare', {
        method: 'POST',
        body: { kind: 'answer', answerId },
        parse: PrepareShareResponse,
      });
      setState({ kind: 'sharing-message' });
      const result = await shareMessageOrUnsupported(prepared.preparedMessageId);
      if (result === 'shared') {
        hapticNotification('success');
        setState({ kind: 'notice', tone: 'info', text: t.share_chat_sent });
        // Веха 8: авторское share-событие через чат (канал 'chat').
        void emitEvent({ type: 'chat_shared', channel: 'chat' });
      } else if (result === 'unsupported') {
        setState({ kind: 'notice', tone: 'info', text: t.share_chat_unsupported });
      } else {
        hapticNotification('error');
        setState({ kind: 'notice', tone: 'error', text: t.share_chat_error });
      }
    } catch (err: unknown) {
      // W1: все ошибки → тост через humanizeApiError (никакого err.message в UI).
      hapticNotification('error');
      showError(humanizeApiError(err, t));
      setState({ kind: 'idle' });
    } finally {
      inFlight.current = false;
    }
  }

  if (cardImageUrl === null) {
    return (
      <List>
        <Placeholder
          header={t.share_title}
          description={t.share_card_rendering_desc}
        >
          <Button mode="bezeled" size="l" onClick={onBackToInbox}>
            {t.share_back}
          </Button>
        </Placeholder>
      </List>
    );
  }

  return (
    <List>
      <Section header={t.share_title}>
        <Caption Component="div" className={styles.hint}>
          {t.share_hint}
        </Caption>

        <img
          src={cardImageUrl}
          alt={t.share_card_alt}
          className={styles.cardImg}
        />

        <div className={styles.btns}>
          {/* ЗАЯВЛЕННОЕ ИСКЛЮЧЕНИЕ: MainButton НЕ применяем, две равноправные кнопки */}
          <Button
            mode="filled"
            size="l"
            stretched
            onClick={handleShareStory}
            disabled={busy}
            loading={state.kind === 'sharing-story'}
          >
            {state.kind === 'sharing-story' ? t.share_story_btn_busy : t.share_story_btn}
          </Button>
          <Button
            mode="bezeled"
            size="l"
            stretched
            onClick={handleShareChat}
            disabled={busy}
            loading={state.kind === 'preparing' || state.kind === 'sharing-message'}
          >
            {state.kind === 'preparing' || state.kind === 'sharing-message'
              ? t.share_chat_btn_busy
              : t.share_chat_btn}
          </Button>

          {state.kind === 'notice' && (
            <Caption
              Component="p"
              role={state.tone === 'error' ? 'alert' : undefined}
              className={[styles.notice, state.tone === 'error' ? styles.noticeError : styles.noticeInfo].join(' ')}
            >
              {state.text}
            </Caption>
          )}
        </div>
      </Section>
    </List>
  );
}
